import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error('Please enter your email'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setSent(true);
        toast.success('Check your inbox for reset instructions');
      } else {
        toast.error(data.detail || 'Something went wrong');
      }
    } catch {
      toast.error('Something went wrong');
    }
    setLoading(false);
  };

  return (
    <div className="pt-20 sm:pt-[122px] min-h-screen bg-cream" data-testid="forgot-password-page">
      <section className="bg-white py-10 sm:py-16 border-b border-cream-dark">
        <div className="max-w-[1632px] mx-auto px-5 sm:px-8 lg:px-12">
          <h1 className="text-4xl sm:text-5xl font-display font-semibold text-[#1A1A1A] mb-4 tracking-tight" data-testid="forgot-password-title">Forgot Password</h1>
          <p className="text-base sm:text-lg text-gray-500 max-w-2xl font-body">Enter your email to receive a password reset link.</p>
        </div>
      </section>

      <div className="flex items-center justify-center px-4 py-6 sm:py-12">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-card p-6 sm:p-8">
          {!sent ? (
            <>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">Email Address</Label>
                  <Input
                    type="email" required placeholder="you@email.com"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 h-11" data-testid="forgot-email-input"
                  />
                </div>
                <Button type="submit" className="w-full h-11 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-full" disabled={loading} data-testid="forgot-submit-btn">
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center" data-testid="forgot-password-sent">
              <div className="w-14 h-14 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-7 h-7 text-teal-600" />
              </div>
              <h2 className="text-xl font-display font-semibold mb-2">Check Your Email</h2>
              <p className="text-sm text-gray-500 mb-6">
                If an account exists for <strong>{email}</strong>, we've sent password reset instructions. The link is valid for 30 minutes.
              </p>
              <button onClick={() => { setSent(false); }} className="block mx-auto text-sm text-gray-500 hover:text-teal-600">
                Try a different email
              </button>
            </div>
          )}

          <div className="mt-6 text-center">
            <Link to="/login" className="inline-flex items-center text-sm text-teal-600 hover:underline" data-testid="back-to-login-link">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to Sign In
            </Link>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
